/**
 * CSS-based page snap for V2 pager animations.
 * Uses native CSS transitions instead of framer-motion animate()
 * for buttery Safari performance.
 */
import type { MotionValue } from 'framer-motion';

/** The CSS transition applied during snaps. */
const SNAP_TRANSITION = 'transform 0.34s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

/**
 * Snap a MotionValue-driven strip to a target x using a native CSS transition.
 *
 * 1. Finds the strip element (first child of container)
 * 2. Applies a CSS transition
 * 3. Sets the MotionValue (which updates inline transform)
 * 4. Cleans up after the transition ends
 */
export function cssSnapTo(
  x: MotionValue<number>,
  targetX: number,
  stripEl: HTMLElement | null,
) {
  if (!stripEl) {
    x.set(targetX);
    return;
  }

  // Apply CSS transition
  stripEl.style.transition = SNAP_TRANSITION;

  // Set the value — framer-motion updates inline transform immediately
  x.set(targetX);

  // Clean up transition after it finishes (or after a safety timeout)
  const cleanup = () => {
    stripEl.style.transition = 'none';
    stripEl.removeEventListener('transitionend', onEnd);
    clearTimeout(fallback);
  };
  const onEnd = (e: TransitionEvent) => {
    // Only respond to the strip's own transform transition, not bubbled child events
    if (e.target === stripEl && e.propertyName === 'transform') cleanup();
  };
  const fallback = setTimeout(cleanup, 400);
  stripEl.addEventListener('transitionend', onEnd);
}

/**
 * Remove CSS transition from strip (call at drag start so finger-tracking is instant).
 */
export function cssDragStart(stripEl: HTMLElement | null) {
  if (stripEl) stripEl.style.transition = 'none';
}
